/**
 * Time formatting utilities
 */

/**
 * Formats seconds into MM:SS format
 * @param seconds - Number of seconds to format
 * @returns Formatted time string in MM:SS format
 *
 * @example
 * formatCountdown(90) // "01:30"
 * formatCountdown(125) // "02:05"
 * formatCountdown(30) // "00:30"
 */
export const formatCountdown = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Formats seconds into HH:MM:SS format
 * @param seconds - Number of seconds to format
 * @returns Formatted time string in HH:MM:SS format
 *
 * @example
 * formatTime(3661) // "01:01:01"
 * formatTime(125) // "00:02:05"
 */
export const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Formats duration in a human-readable format
 * @param seconds - Number of seconds
 * @returns Human-readable duration string
 *
 * @example
 * formatDuration(3661) // "1h 1m 1s"
 * formatDuration(125) // "2m 5s"
 * formatDuration(30) // "30s"
 */
export const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (mins > 0) parts.push(`${mins}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(' ');
};

/**
 * Formats a remaining duration as a days-aware countdown.
 * Once a larger unit is shown, all smaller units down to seconds are included.
 * @param seconds - Number of seconds remaining (negatives are clamped to 0)
 * @returns Countdown string
 *
 * @example
 * formatExpiryCountdown(585838) // "6d 18h 43m 58s"
 * formatExpiryCountdown(3661)   // "1h 1m 1s"
 * formatExpiryCountdown(58)     // "58s"
 */
export const formatExpiryCountdown = (seconds: number): string => {
  const safe = Math.max(0, Math.floor(seconds));
  const days = Math.floor(safe / 86400);
  const hours = Math.floor((safe % 86400) / 3600);
  const mins = Math.floor((safe % 3600) / 60);
  const secs = safe % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (parts.length > 0 || hours > 0) parts.push(`${hours}h`);
  if (parts.length > 0 || mins > 0) parts.push(`${mins}m`);
  parts.push(`${secs}s`);

  return parts.join(' ');
};
